import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import moment from "moment";
import ReactPaginate from "react-paginate";
import {
  Modal,
  Button,
  Form,
  Spinner,
  Alert,
  Table,
  Badge,
  Card,
  Row,
  Col,
  Tabs,
  Tab,
  Accordion,
  ProgressBar,
} from "react-bootstrap";
import "./HubList.css";
import AreaSelector from "../Map/AreaSelector";

const HubList = () => {
  // Modal states
  const [showAddHub, setShowAddHub] = useState(false);
  const [showEditHub, setShowEditHub] = useState(false);
  const [showDeleteHub, setShowDeleteHub] = useState(false);
  const [showViewAllPolygons, setShowViewAllPolygons] = useState(false);
  const [showCutoffSettings, setShowCutoffSettings] = useState(false);
  const [showOrderModeModal, setShowOrderModeModal] = useState(false);
  const [showMinCartModal, setShowMinCartModal] = useState(false);
  const [showBulkMinCartModal, setShowBulkMinCartModal] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [showOfferStatsModal, setShowOfferStatsModal] = useState(false);

  // Hub data states
  const [hubs, setHubs] = useState([]);
  const [noChangeData, setNoChangeData] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  // Add/Edit/Delete hub states
  const [newHub, setNewHub] = useState({
    hubName: "",
    counterName: "",
    hubType: null,
    locations: [],
    geometry: null,
    orderMode: "preorder",
    minCart: 0,
    hubOffer: null,
    cutoffTimes: {
      breakfast: {
        defaultCutoff: "00:00",
        employeeCutoff: "10:00",
      },
      lunch: {
        defaultCutoff: "00:00",
        employeeCutoff: "10:00",
      },
      dinner: {
        defaultCutoff: "00:00",
        employeeCutoff: "10:00",
      },
    },
  });

  const [editHub, setEditHub] = useState({
    hubId: "",
    hubName: "",
    counterName: "",
    hubType: null,
    locations: [],
    geometry: null,
    orderMode: "preorder",
    minCart: 0,
    hubOffer: null,
    cutoffTimes: {
      breakfast: {
        defaultCutoff: "00:00",
        employeeCutoff: "10:00",
      },
      lunch: {
        defaultCutoff: "00:00",
        employeeCutoff: "10:00",
      },
      dinner: {
        defaultCutoff: "00:00",
        employeeCutoff: "10:00",
      },
    },
  });

  const [selectedHub, setSelectedHub] = useState(null);
  const [selectedHubForCutoff, setSelectedHubForCutoff] = useState(null);
  const [selectedHubForOrderMode, setSelectedHubForOrderMode] = useState(null);
  const [selectedHubForMinCart, setSelectedHubForMinCart] = useState(null);
  const [selectedHubForOffer, setSelectedHubForOffer] = useState(null);
  const [selectedHubForStats, setSelectedHubForStats] = useState(null);
  const [bulkMinCartValue, setBulkMinCartValue] = useState(0);

  // Offer configuration state
  const [offerConfig, setOfferConfig] = useState({
    userCap: "",
    offerCap: "",
    validity: "",
    mealsPerValidity: "",
    isActive: true,
    startDate: "",
    endDate: "",
  });

  // Seeds the validity field when it's still blank: the number of days the campaign
  // runs, counting both the start and end date (Jan 1 → Jan 7 = 7 days). Validity
  // itself is per-customer (days from their signup), so the admin can type anything —
  // a 7-day validity inside a 30-day campaign is normal. Returns "" until both dates
  // are set and valid.
  const computeValidityDays = (start, end) => {
    if (!start || !end) return "";
    const s = new Date(start);
    const e = new Date(end);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()) || e < s)
      return "";
    return Math.round((e - s) / 86400000) + 1;
  };

  const [offerStats, setOfferStats] = useState(null);

  const [addHubLoading, setAddHubLoading] = useState(false);
  const [editHubLoading, setEditHubLoading] = useState(false);
  const [deleteHubLoading, setDeleteHubLoading] = useState(false);
  const [cutoffLoading, setCutoffLoading] = useState(false);
  const [orderModeLoading, setOrderModeLoading] = useState(false);
  const [minCartLoading, setMinCartLoading] = useState(false);
  const [bulkMinCartLoading, setBulkMinCartLoading] = useState(false);
  const [offerLoading, setOfferLoading] = useState(false);

  // Toast state
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  // Polygon reassignment states
  const [originalGeometry, setOriginalGeometry] = useState(null);
  const [showReassignPreview, setShowReassignPreview] = useState(false);
  const [reassignPreview, setReassignPreview] = useState(null);
  const [reassignLoading, setReassignLoading] = useState(false);

  // Unserved users panel states
  const [showUnservedPanel, setShowUnservedPanel] = useState(false);
  const [unservedUsers, setUnservedUsers] = useState([]);
  const [unservedLoading, setUnservedLoading] = useState(false);

  // Pagination states
  const [pageNumber, setPageNumber] = useState(0);
  const hubsPerPage = 10;
  const pagesVisited = pageNumber * hubsPerPage;
  const pageCount = Math.ceil(hubs.length / hubsPerPage);

  // Location data states
  const [corporateLocations, setCorporateLocations] = useState([]);
  const [apartmentLocations, setApartmentLocations] = useState([]);
  const [allLocations, setAllLocations] = useState([]);

  // Token from localStorage
  const token = localStorage.getItem("authToken");

  // Fetch corporate locations
  const getCorporateLocations = useCallback(async () => {
    try {
      const res = await axios.get(
        "https://dailydish.in/api/admin/getcorporate",
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (res.status === 200) {
        setCorporateLocations(res.data.corporatedata);
      }
    } catch (error) {
      console.error("Error fetching corporate locations:", error);
      showToast("Failed to fetch corporate locations.", "error");
    }
  }, [token]);

  // Fetch apartment locations
  const getApartmentLocations = useCallback(async () => {
    try {
      const res = await axios.get(
        "https://dailydish.in/api/admin/getapartment",
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (res.status === 200) {
        setApartmentLocations(res.data.corporatedata);
      }
    } catch (error) {
      console.error("Error fetching apartment locations:", error);
      showToast("Failed to fetch apartment locations.", "error");
    }
  }, [token]);

  // Combine all locations
  useEffect(() => {
    const combinedLocations = [
      ...corporateLocations.map((loc) => ({
        value: `${loc.Apartmentname}, ${loc.Address}, ${loc.pincode}`,
        label: `${loc.Apartmentname}, ${loc.Address}, ${loc.pincode}`,
        type: "Corporate",
      })),
      ...apartmentLocations.map((loc) => ({
        value: `${loc.Apartmentname}, ${loc.Address}, ${loc.pincode}`,
        label: `${loc.Apartmentname}, ${loc.Address}, ${loc.pincode}`,
        type: "Apartment",
      })),
    ];
    setAllLocations(combinedLocations);
  }, [corporateLocations, apartmentLocations]);

  // Show toast notification
  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
  };

  // Fetch hubs
  const getHubs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("https://dailydish.in/api/Hub/hubs", {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log(res, "responnnnnnnnnnnnnnnnnnnnnnnn");
      setHubs(res.data);
      setNoChangeData(res.data);
    } catch (error) {
      showToast(
        error?.response?.data?.message || "Failed to fetch hubs.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Get default cutoff times based on order mode
  const getDefaultCutoffTimes = (orderMode) => {
    const defaultCutoff = orderMode === "preorder" ? "00:00" : "10:00";
    const employeeCutoff = "10:00";

    return {
      breakfast: { defaultCutoff, employeeCutoff },
      lunch: { defaultCutoff, employeeCutoff },
      dinner: { defaultCutoff, employeeCutoff },
    };
  };

  // Handle order mode change for new hub
  const handleNewHubOrderModeChange = (mode) => {
    const defaultCutoffTimes = getDefaultCutoffTimes(mode);
    setNewHub({
      ...newHub,
      orderMode: mode,
      cutoffTimes: defaultCutoffTimes,
    });
  };

  // Handle order mode change for edit hub
  const handleEditHubOrderModeChange = (mode) => {
    const defaultCutoffTimes = getDefaultCutoffTimes(mode);
    setEditHub({
      ...editHub,
      orderMode: mode,
      cutoffTimes: defaultCutoffTimes,
    });
  };

  // Add Hub
  const handleAddHub = async () => {
    if (!newHub.hubName.trim()) {
      showToast("Hub name is required.", "error");
      return;
    }
    if (!newHub.geometry) {
      showToast("Please draw a service area polygon on the map.", "error");
      return;
    }
    if (newHub.minCart < 0 || isNaN(newHub.minCart)) {
      showToast("Minimum cart value must be a non-negative number.", "error");
      return;
    }

    // Validate offer if provided
    if (newHub.hubOffer) {
      if (!newHub.hubOffer.userCap || newHub.hubOffer.userCap < 1) {
        showToast("User cap must be at least 1", "error");
        return;
      }
      if (
        newHub.hubOffer.offerCap === undefined ||
        newHub.hubOffer.offerCap < 0
      ) {
        showToast("Offer cap must be a non-negative number", "error");
        return;
      }
      if (
        !newHub.hubOffer.mealsPerValidity ||
        newHub.hubOffer.mealsPerValidity < 1
      ) {
        showToast("Meals per validity must be at least 1", "error");
        return;
      }
      if (!newHub.hubOffer.startDate) {
        showToast("Campaign start date is required", "error");
        return;
      }
      if (!newHub.hubOffer.endDate) {
        showToast("Campaign end date is required", "error");
        return;
      }
      if (
        new Date(newHub.hubOffer.endDate) <= new Date(newHub.hubOffer.startDate)
      ) {
        showToast("End date must be after start date", "error");
        return;
      }
      if (!newHub.hubOffer.validity || newHub.hubOffer.validity < 1) {
        showToast("Validity must be at least 1 day", "error");
        return;
      }
    }

    setAddHubLoading(true);
    try {
      const payload = {
        hubName: newHub.hubName.trim(),
        counterName: newHub.counterName.trim() || null,
        hubType: newHub.hubType || null,
        locations: newHub.locations,
        geometry: newHub.geometry,
        orderMode: newHub.orderMode,
        minCart: newHub.minCart,
        cutoffTimes: newHub.cutoffTimes,
      };

      if (newHub.hubOffer) {
        payload.hubOffer = newHub.hubOffer;
      }

      const res = await axios.post(
        "https://dailydish.in/api/Hub/hubs",
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.status === 201) {
        const assigned = res.data.assignedFromUnserved;
        const overlaps = res.data.overlappingHubs;
        let msg =
          assigned > 0
            ? `Hub added. ${assigned} previously unserved user${assigned > 1 ? "s" : ""} assigned to this hub.`
            : "Hub added successfully";
        if (overlaps?.length > 0) {
          msg += ` Warning: polygon overlaps with ${overlaps.join(", ")}.`;
        }
        showToast(msg, overlaps?.length > 0 ? "warning" : "success");
        setShowAddHub(false);
        setNewHub({
          hubName: "",
          counterName: "",
          hubType: null,
          locations: [],
          geometry: null,
          orderMode: "preorder",
          minCart: 0,
          hubOffer: null,
          cutoffTimes: getDefaultCutoffTimes("preorder"),
        });
        getHubs();
      }
    } catch (error) {
      showToast(
        error?.response?.data?.message || "Failed to add hub.",
        "error",
      );
    } finally {
      setAddHubLoading(false);
    }
  };

  // Edit Hub — Phase 1: check if polygon changed and show impact preview
  const handleEditHub = async () => {
    if (!editHub.hubName.trim()) {
      showToast("Hub name is required.", "error");
      return;
    }
    if (editHub.minCart < 0 || isNaN(editHub.minCart)) {
      showToast("Minimum cart value must be a non-negative number.", "error");
      return;
    }

    const geometryChanged =
      editHub.geometry &&
      JSON.stringify(editHub.geometry) !== JSON.stringify(originalGeometry);

    if (geometryChanged) {
      setReassignLoading(true);
      try {
        const previewRes = await axios.post(
          `https://dailydish.in/api/Hub/hubs/${editHub.hubId}/reassign-preview`,
          { geometry: editHub.geometry },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const p = previewRes.data.preview;
        const anyAffected =
          p.addedCount > 0 || p.removedToOtherHub > 0 || p.removedToNoHub > 0;
        if (anyAffected) {
          setReassignPreview(p);
          setShowReassignPreview(true);
          return; // Wait for user confirmation
        }
        // No users affected — proceed to save directly
      } catch {
        // Preview failed silently — proceed with save
      } finally {
        setReassignLoading(false);
      }
    }

    await doSaveHub();
  };

  // Edit Hub — Phase 2: perform the actual save (called directly or after modal confirmation)
  const doSaveHub = async () => {
    setEditHubLoading(true);
    setShowReassignPreview(false);
    try {
      const payload = {
        hubName: editHub.hubName.trim(),
        counterName: editHub.counterName?.trim() || null,
        hubType: editHub.hubType || null,
        locations: editHub.locations || [],
        orderMode: editHub.orderMode,
        minCart: editHub.minCart,
      };

      if (editHub.geometry) {
        payload.geometry = editHub.geometry;
      }

      const res = await axios.put(
        `https://dailydish.in/api/Hub/hubs/${editHub.hubId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.status === 200) {
        const r = res.data.reassignment;
        let msg = "Hub updated successfully";
        if (r) {
          const parts = [];
          if (r.addedCount > 0)
            parts.push(
              `${r.addedCount} user${r.addedCount > 1 ? "s" : ""} added`,
            );
          if (r.removedToOtherHub > 0)
            parts.push(`${r.removedToOtherHub} moved to another hub`);
          if (r.removedToNoHub > 0)
            parts.push(`${r.removedToNoHub} now unserved`);
          if (parts.length) msg = `Hub updated. ${parts.join(", ")}.`;
        }
        showToast(msg);
        // Clear cached primary address so the user's next page load fetches fresh hub data
        localStorage.removeItem("primaryAddress");
        setShowEditHub(false);
        setReassignPreview(null);
        setOriginalGeometry(null);
        getHubs();
      }
    } catch (error) {
      showToast(
        error?.response?.data?.message || "Failed to update hub.",
        "error",
      );
    } finally {
      setEditHubLoading(false);
    }
  };

  const fetchUnservedUsers = async () => {
    setUnservedLoading(true);
    try {
      const res = await axios.get(
        "https://dailydish.in/api/Hub/hubs/unserved-users",
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setUnservedUsers(res.data.users || []);
    } catch {
      showToast("Failed to load unserved users.", "error");
    } finally {
      setUnservedLoading(false);
    }
  };

  const handleOpenUnservedPanel = () => {
    setShowUnservedPanel(true);
    fetchUnservedUsers();
  };

  // Update Order Mode
  const handleUpdateOrderMode = async () => {
    if (!selectedHubForOrderMode) return;

    setOrderModeLoading(true);
    try {
      const res = await axios.put(
        `https://dailydish.in/api/Hub/update-order-mode/${selectedHubForOrderMode.hubId}`,
        {
          orderMode: selectedHubForOrderMode.orderMode,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (res.status === 200) {
        showToast(
          `Order mode updated to ${selectedHubForOrderMode.orderMode}. ` +
            `${res.data.propagationStats?.customers || 0} customer addresses, ` +
            `${res.data.propagationStats?.orders || 0} orders, and ` +
            `${res.data.propagationStats?.mealPlans || 0} meal plans updated.`,
        );
        setShowOrderModeModal(false);
        getHubs();
      }
    } catch (error) {
      showToast(
        error?.response?.data?.message || "Failed to update order mode.",
        "error",
      );
    } finally {
      setOrderModeLoading(false);
    }
  };

  // Update Min Cart
  const handleUpdateMinCart = async () => {
    if (!selectedHubForMinCart) return;

    if (
      selectedHubForMinCart.minCart < 0 ||
      isNaN(selectedHubForMinCart.minCart)
    ) {
      showToast("Minimum cart value must be a non-negative number.", "error");
      return;
    }

    setMinCartLoading(true);
    try {
      const res = await axios.put(
        `https://dailydish.in/api/Hub/update-min-cart/${selectedHubForMinCart.hubId}`,
        {
          minCart: selectedHubForMinCart.minCart,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (res.status === 200) {
        showToast(
          `Minimum cart value updated to ₹${selectedHubForMinCart.minCart}. ` +
            `${res.data.propagationStats?.customers || 0} customer addresses, ` +
            `${res.data.propagationStats?.orders || 0} orders, and ` +
            `${res.data.propagationStats?.mealPlans || 0} meal plans updated.`,
        );
        setShowMinCartModal(false);
        getHubs();
      }
    } catch (error) {
      showToast(
        error?.response?.data?.message ||
          "Failed to update minimum cart value.",
        "error",
      );
    } finally {
      setMinCartLoading(false);
    }
  };

  // Bulk Update Min Cart
  const handleBulkUpdateMinCart = async () => {
    if (bulkMinCartValue < 0 || isNaN(bulkMinCartValue)) {
      showToast("Minimum cart value must be a non-negative number.", "error");
      return;
    }

    setBulkMinCartLoading(true);
    try {
      const res = await axios.put(
        "https://dailydish.in/api/Hub/bulk-update-min-cart",
        {
          minCart: bulkMinCartValue,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (res.status === 200) {
        showToast(
          `Bulk update completed! ${res.data.modifiedCount} hubs updated. ` +
            `${res.data.propagationStats?.customers || 0} customer addresses, ` +
            `${res.data.propagationStats?.orders || 0} orders, and ` +
            `${res.data.propagationStats?.mealPlans || 0} meal plans updated.`,
        );
        setShowBulkMinCartModal(false);
        setBulkMinCartValue(0);
        getHubs();
      }
    } catch (error) {
      showToast(
        error?.response?.data?.message ||
          "Failed to bulk update minimum cart value.",
        "error",
      );
    } finally {
      setBulkMinCartLoading(false);
    }
  };

  // Update Hub Offer
  const handleUpdateHubOffer = async () => {
    if (!selectedHubForOffer) return;

    if (!offerConfig.userCap || offerConfig.userCap < 1) {
      showToast("User cap must be at least 1", "error");
      return;
    }
    if (offerConfig.offerCap === undefined || offerConfig.offerCap < 0) {
      showToast("Offer cap must be a non-negative number", "error");
      return;
    }
    if (!offerConfig.mealsPerValidity || offerConfig.mealsPerValidity < 1) {
      showToast("Meals per validity must be at least 1", "error");
      return;
    }
    if (!offerConfig.startDate) {
      showToast("Campaign start date is required", "error");
      return;
    }
    if (!offerConfig.endDate) {
      showToast("Campaign end date is required", "error");
      return;
    }
    if (new Date(offerConfig.endDate) <= new Date(offerConfig.startDate)) {
      showToast("End date must be after start date", "error");
      return;
    }
    if (!offerConfig.validity || offerConfig.validity < 1) {
      showToast("Validity must be at least 1 day", "error");
      return;
    }

    setOfferLoading(true);
    try {
      const res = await axios.put(
        `https://dailydish.in/api/Hub/hubs/${selectedHubForOffer.hubId}/offer`,
        {
          hubOffer: {
            userCap: parseInt(offerConfig.userCap),
            offerCap: parseFloat(offerConfig.offerCap),
            validity: parseInt(offerConfig.validity),
            mealsPerValidity: parseInt(offerConfig.mealsPerValidity),
            isActive: offerConfig.isActive,
            startDate: offerConfig.startDate,
            endDate: offerConfig.endDate,
          },
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (res.status === 200) {
        showToast("Hub offer updated successfully");
        setShowOfferModal(false);
        getHubs();
      }
    } catch (error) {
      showToast(
        error?.response?.data?.message || "Failed to update hub offer.",
        "error",
      );
    } finally {
      setOfferLoading(false);
    }
  };

  // Disable Hub Offer
  const handleDisableOffer = async (hubId) => {
    setOfferLoading(true);
    try {
      const res = await axios.post(
        `https://dailydish.in/api/Hub/hubs/${hubId}/offer/disable`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (res.status === 200) {
        showToast("Offer disabled successfully");
        getHubs();
      }
    } catch (error) {
      showToast(
        error?.response?.data?.message || "Failed to disable offer.",
        "error",
      );
    } finally {
      setOfferLoading(false);
    }
  };

  // Enable Hub Offer
  const handleEnableOffer = async (hubId) => {
    setOfferLoading(true);
    try {
      const res = await axios.post(
        `https://dailydish.in/api/Hub/hubs/${hubId}/offer/enable`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (res.status === 200) {
        showToast("Offer enabled successfully");
        getHubs();
      }
    } catch (error) {
      showToast(
        error?.response?.data?.message || "Failed to enable offer.",
        "error",
      );
    } finally {
      setOfferLoading(false);
    }
  };

  // Fetch Offer Stats
  const fetchOfferStats = async (hubId) => {
    setOfferLoading(true);
    try {
      const res = await axios.get(
        `https://dailydish.in/api/Hub/hubs/${hubId}/offer/stats`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (res.status === 200) {
        setOfferStats(res.data);
        setShowOfferStatsModal(true);
      }
    } catch (error) {
      showToast(
        error?.response?.data?.message || "Failed to fetch offer stats.",
        "error",
      );
    } finally {
      setOfferLoading(false);
    }
  };

  // Delete Hub
  const handleDeleteHub = async () => {
    setDeleteHubLoading(true);
    try {
      const res = await axios.delete(
        `https://dailydish.in/api/Hub/hubs/${selectedHub.hubId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (res.status === 200) {
        showToast("Hub deleted successfully");
        setShowDeleteHub(false);
        setSelectedHub(null);
        getHubs();
      }
    } catch (error) {
      showToast(
        error?.response?.data?.message || "Failed to delete hub.",
        "error",
      );
    } finally {
      setDeleteHubLoading(false);
    }
  };

  // Update Cutoff Times
  const handleUpdateCutoffTimes = async () => {
    if (!selectedHubForCutoff) return;

    setCutoffLoading(true);
    try {
      const res = await axios.put(
        `https://dailydish.in/api/Hub/update-cutoff-times/${selectedHubForCutoff.hubId}`,
        {
          cutoffTimes: selectedHubForCutoff.cutoffTimes,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (res.status === 200) {
        showToast("Cutoff times updated successfully");
        setShowCutoffSettings(false);
        getHubs();
      }
    } catch (error) {
      showToast(
        error?.response?.data?.message || "Failed to update cutoff times.",
        "error",
      );
    } finally {
      setCutoffLoading(false);
    }
  };

  // Search filter
  const handleFilter = (e) => {
    const searchTerm = e.target.value.toLowerCase();
    setSearch(searchTerm);
    setPageNumber(0);
    if (searchTerm) {
      const filteredData = noChangeData.filter((hub) => {
        const hubName = hub.hubName ? hub.hubName.toLowerCase() : "";
        const hubId = hub.hubId ? hub.hubId.toLowerCase() : "";
        const locations = hub.locations
          ? hub.locations.join(" ").toLowerCase()
          : "";
        return (
          hubName.includes(searchTerm) ||
          hubId.includes(searchTerm) ||
          locations.includes(searchTerm)
        );
      });
      setHubs(filteredData);
    } else {
      setHubs(noChangeData);
    }
  };

  // Export Excel
  const handleExportExcel = () => {
    setLoading(true);
    try {
      const customHeaders = noChangeData.map((item) => ({
        "Hub ID": item.hubId || "N/A",
        "Hub Name": item.hubName || "N/A",
        "Order Mode": item.orderMode || "preorder",
        "Minimum Cart (₹)": item.minCart || 0,
        "Has Offer": item.hubOffer ? "Yes" : "No",
        "Offer User Cap": item.hubOffer?.userCap || "N/A",
        "Offer Cap (₹)": item.hubOffer?.offerCap || "N/A",
        "Offer Validity (days)": item.hubOffer?.validity || "N/A",
        "Meals per Validity": item.hubOffer?.mealsPerValidity || "N/A",
        "Offer Active": item.hubOffer?.isActive ? "Yes" : "No",
        "Users Claimed": item.hubOffer?.currentUserCount || 0,
        "Remaining Spots": item.hubOffer?.remainingSpots || "N/A",
        "Total Locations": item.locations ? item.locations.length : 0,
        Locations: item.locations ? item.locations.join(", ") : "N/A",
        "Breakfast Default Cutoff":
          item.cutoffTimes?.breakfast?.defaultCutoff || "00:00",
        "Breakfast Employee Cutoff":
          item.cutoffTimes?.breakfast?.employeeCutoff || "10:00",
        "Lunch Default Cutoff":
          item.cutoffTimes?.lunch?.defaultCutoff || "00:00",
        "Lunch Employee Cutoff":
          item.cutoffTimes?.lunch?.employeeCutoff || "10:00",
        "Dinner Default Cutoff":
          item.cutoffTimes?.dinner?.defaultCutoff || "00:00",
        "Dinner Employee Cutoff":
          item.cutoffTimes?.dinner?.employeeCutoff || "10:00",
      }));
      const worksheet = XLSX.utils.json_to_sheet(customHeaders);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Hub List");
      XLSX.writeFile(workbook, `HubList_${moment().format("YYYYMMDD")}.xlsx`);
      showToast("Exported to Excel successfully");
    } catch (e) {
      console.error(e);
      showToast("Failed to export to Excel.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Pagination
  const changePage = ({ selected }) => setPageNumber(selected);

  // Fetch data on mount
  useEffect(() => {
    getHubs();
    getCorporateLocations();
    getApartmentLocations();
  }, [getHubs, getCorporateLocations, getApartmentLocations]);

  // Get location type badge
  const getLocationBadge = (location) => {
    const locationData = allLocations.find((loc) => loc.value === location);
    return locationData ? (
      <Badge
        bg={locationData.type === "Corporate" ? "info" : "success"}
        className="me-1 mb-1"
      >
        {locationData.type}
      </Badge>
    ) : null;
  };

  // Get order mode badge
  const getOrderModeBadge = (orderMode) => {
    return orderMode === "preorder" ? (
      <Badge bg="warning" text="dark">
        📋 Preorder
      </Badge>
    ) : (
      <Badge bg="success">⚡ Instant</Badge>
    );
  };

  // Get offer badge
  const getOfferBadge = (hubOffer) => {
    if (!hubOffer) {
      return <Badge bg="secondary">No Offer</Badge>;
    }
    if (hubOffer.isActive && hubOffer.remainingSpots > 0) {
      return (
        <Badge bg="success">
          ✓ Active ({hubOffer.remainingSpots} spots left)
        </Badge>
      );
    }
    if (!hubOffer.isActive && hubOffer.currentUserCount >= hubOffer.userCap) {
      return (
        <Badge bg="danger">
          Full ({hubOffer.userCap}/{hubOffer.userCap})
        </Badge>
      );
    }
    return (
      <Badge bg="warning" text="dark">
        Inactive
      </Badge>
    );
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Format offer cap display
  const formatOfferCap = (cap) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(cap);
  };

  // Order Mode Selector Component
  const OrderModeSelector = ({ orderMode, onChange, disabled = false }) => {
    return (
      <div className="order-mode-selector mb-3">
        <Form.Label className="fw-bold">Order Mode</Form.Label>
        <div className="d-flex gap-3">
          <Form.Check
            type="radio"
            id="preorder-mode"
            label={
              <span>
                <Badge bg="warning" text="dark" className="me-2">
                  📋 Preorder
                </Badge>
                <span className="text-muted">
                  (Default cutoff: Previous day midnight)
                </span>
              </span>
            }
            name="orderMode"
            value="preorder"
            checked={orderMode === "preorder"}
            onChange={() => onChange("preorder")}
            disabled={disabled}
          />
          <Form.Check
            type="radio"
            id="instant-mode"
            label={
              <span>
                <Badge bg="success" className="me-2">
                  ⚡ Instant
                </Badge>
                <span className="text-muted">
                  (Default cutoff: Same day 10:00 AM)
                </span>
              </span>
            }
            name="orderMode"
            value="instant"
            checked={orderMode === "instant"}
            onChange={() => onChange("instant")}
            disabled={disabled}
          />
        </div>
        <Form.Text className="text-muted">
          {orderMode === "preorder"
            ? "Preorder mode: Customers must order by previous day midnight. Employees can order same day until 10:00 AM."
            : "Instant mode: Both customers and employees can order same day until cutoff time (default 10:00 AM)."}
        </Form.Text>
      </div>
    );
  };

  // Cutoff Time Input Component
  const CutoffTimeInput = ({
    label,
    session,
    cutoffTimes,
    onChange,
    orderMode,
  }) => {
    return (
      <div className="mb-3 p-3 border rounded">
        <h6 className="mb-3">{label}</h6>
        <Row>
          <Col md={6}>
            <Form.Group>
              <Form.Label className="small">
                <Badge bg="secondary" className="me-1">
                  Regular Customers
                </Badge>
                Cutoff Time
                {orderMode === "preorder" && (
                  <Badge bg="info" className="ms-1">
                    Previous Day
                  </Badge>
                )}
              </Form.Label>
              <Form.Control
                type="time"
                value={cutoffTimes[session]?.defaultCutoff || "00:00"}
                onChange={(e) =>
                  onChange(session, "defaultCutoff", e.target.value)
                }
                step="60"
              />
              <Form.Text className="text-muted small">
                {orderMode === "preorder"
                  ? "Orders must be placed by this time on the previous day"
                  : "Orders must be placed by this time on the same day"}
              </Form.Text>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label className="small">
                <Badge bg="primary" className="me-1">
                  Employees
                </Badge>
                Cutoff Time (Same Day)
              </Form.Label>
              <Form.Control
                type="time"
                value={cutoffTimes[session]?.employeeCutoff || "10:00"}
                onChange={(e) =>
                  onChange(session, "employeeCutoff", e.target.value)
                }
                step="60"
              />
              <Form.Text className="text-muted small">
                Employees can order on the same day until this time
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        {orderMode === "instant" && (
          <div
            className="mt-3 p-2 rounded"
            style={{ background: "#fff8f0", border: "1px solid #fe4500" }}
          >
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="small fw-semibold" style={{ color: "#fe4500" }}>
                Post-Cutoff Window
              </span>
              <Form.Check
                type="switch"
                id={`window-enabled-${session}`}
                label={
                  cutoffTimes[session]?.windowEnabled ? "Enabled" : "Disabled"
                }
                checked={Boolean(cutoffTimes[session]?.windowEnabled)}
                onChange={(e) =>
                  onChange(session, "windowEnabled", e.target.checked)
                }
              />
            </div>
            {cutoffTimes[session]?.windowEnabled && (
              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small text-muted">
                      Window Start (auto = Regular Cutoff)
                    </Form.Label>
                    <Form.Control
                      type="time"
                      value={cutoffTimes[session]?.defaultCutoff || "00:00"}
                      disabled
                      style={{ background: "#f0f0f0" }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small text-muted">
                      Window End Time <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="time"
                      value={cutoffTimes[session]?.windowEnd || ""}
                      onChange={(e) =>
                        onChange(session, "windowEnd", e.target.value)
                      }
                      step="60"
                    />
                    <Form.Text className="text-muted small">
                      Window products visible until this time
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            )}
          </div>
        )}
      </div>
    );
  };

  // Location selector component
  const LocationSelector = ({
    selectedLocations,
    onLocationChange,
    disabled = false,
  }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const filteredLocations = allLocations.filter((location) =>
      location.label.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    const handleLocationToggle = (locationValue) => {
      const newLocations = selectedLocations.includes(locationValue)
        ? selectedLocations.filter((loc) => loc !== locationValue)
        : [...selectedLocations, locationValue];
      onLocationChange(newLocations);
    };

    return (
      <div className="location-selector">
        <div className="selected-locations mb-2">
          {selectedLocations.map((location) => (
            <Badge
              key={location}
              bg="primary"
              className="me-1 mb-1 d-flex align-items-center"
              style={{ fontSize: "0.75rem" }}
            >
              {location.split(",")[0]}
              <button
                type="button"
                className="btn-close btn-close-white ms-1"
                style={{ fontSize: "0.5rem" }}
                onClick={() => handleLocationToggle(location)}
                disabled={disabled}
              />
            </Badge>
          ))}
        </div>
        <div className="dropdown">
          <Form.Control
            type="text"
            placeholder="Search and select locations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setIsDropdownOpen(true)}
            disabled={disabled}
          />
          {isDropdownOpen && (
            <div
              className="dropdown-menu show w-100"
              style={{ maxHeight: "200px", overflowY: "auto" }}
            >
              {filteredLocations.map((location) => (
                <div
                  key={location.value}
                  className={`dropdown-item d-flex align-items-center justify-content-between ${
                    selectedLocations.includes(location.value) ? "active" : ""
                  }`}
                  onClick={() => handleLocationToggle(location.value)}
                  style={{ cursor: "pointer" }}
                >
                  <span>{location.label}</span>
                  <Badge
                    bg={location.type === "Corporate" ? "info" : "success"}
                  >
                    {location.type}
                  </Badge>
                </div>
              ))}
              {filteredLocations.length === 0 && (
                <div className="dropdown-item text-muted">
                  No locations found
                </div>
              )}
            </div>
          )}
        </div>
        <div className="d-flex justify-content-end mt-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setIsDropdownOpen(false)}
          >
            Close
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="hub-list">
      {/* Toast Notification */}
      <Alert
        variant={toast.type === "success" ? "success" : "danger"}
        show={toast.show}
        className="hub-list-toast position-fixed"
        style={{ top: "20px", right: "20px", zIndex: 1050 }}
      >
        {toast.message}
      </Alert>

      {/* Loading Overlay */}
      {loading && (
        <div
          className="hub-list-loading-overlay position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1040 }}
        >
          <div className="text-center">
            <Spinner animation="border" variant="light" />
            <div className="text-light mt-2">Loading...</div>
          </div>
        </div>
      )}

      <Card className="shadow-sm">
        <Card.Header className="text-white" style={{ background: "#fe4500" }}>
          <Row className="align-items-center">
            <Col>
              <h4 className="mb-0">Hub Management</h4>
            </Col>
            <Col xs="auto">
              <div className="d-flex gap-2">
                <Button
                  variant="outline-light"
                  onClick={() => setShowViewAllPolygons(true)}
                  disabled={loading}
                  className="d-flex align-items-center"
                >
                  🗺️ View All Polygons
                </Button>
                <Button
                  variant="outline-light"
                  onClick={() => setShowBulkMinCartModal(true)}
                  disabled={loading}
                  className="d-flex align-items-center"
                >
                  💰 Bulk Update Min Cart
                </Button>
                <Button
                  variant="outline-light"
                  onClick={handleExportExcel}
                  disabled={loading}
                  className="d-flex align-items-center"
                >
                  {loading ? (
                    <Spinner animation="border" size="sm" className="me-2" />
                  ) : null}
                  Export Excel
                </Button>
                <Button
                  variant="outline-warning"
                  onClick={handleOpenUnservedPanel}
                  disabled={loading}
                  title="View addresses outside all delivery zones"
                >
                  Unserved Users
                </Button>
                <Button
                  variant="light"
                  onClick={() => setShowAddHub(true)}
                  disabled={loading}
                >
                  + Add Hub
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Header>

        <Card.Body>
          {/* Search and Stats */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Control
                type="text"
                placeholder="🔍 Search by Hub Name, ID, or Locations..."
                value={search}
                onChange={handleFilter}
                className="shadow-sm"
              />
            </Col>
            <Col
              md={6}
              className="d-flex align-items-center justify-content-end"
            >
              <div className="text-muted">
                <strong>{hubs.length}</strong> hubs found
              </div>
            </Col>
          </Row>

          {/* Hub Table */}
          <div className="table-responsive">
            <Table striped hover className="shadow-sm">
              <thead className="table-dark">
                <tr>
                  <th width="5%">SL.NO</th>
                  <th width="7%">Hub ID</th>
                  <th width="9%">Hub Name</th>
                  <th width="6%">Counter</th>
                  <th width="7%">Type</th>
                  <th width="7%">Mode</th>
                  <th width="7%">Min Cart</th>
                  <th width="10%">Offer</th>
                  <th width="14%">Cutoff Times</th>
                  <th width="28%">Actions</th>
                </tr>
              </thead>
              <tbody>
                {hubs.length > 0 ? (
                  hubs
                    .slice(pagesVisited, pagesVisited + hubsPerPage)
                    .map((hub, i) => (
                      <tr key={hub._id}>
                        <td className="align-middle">{i + 1 + pagesVisited}</td>
                        <td className="align-middle">
                          <Badge bg="secondary">{hub.hubId || "N/A"}</Badge>
                        </td>
                        <td className="align-middle">
                          <strong>{hub.hubName || "N/A"}</strong>
                        </td>
                        <td className="align-middle">
                          {hub.counterName ? (
                            <Badge
                              bg="dark"
                              style={{
                                fontFamily: "monospace",
                                letterSpacing: "0.5px",
                              }}
                            >
                              {hub.counterName}
                            </Badge>
                          ) : (
                            <span style={{ color: "#aaa", fontSize: "12px" }}>
                              —
                            </span>
                          )}
                        </td>
                        <td className="align-middle">
                          {hub.hubType ? (
                            <Badge
                              bg={
                                hub.hubType === "school"
                                  ? "primary"
                                  : hub.hubType === "office"
                                    ? "info"
                                    : hub.hubType === "apartment"
                                      ? "success"
                                      : "secondary"
                              }
                            >
                              {hub.hubType.charAt(0).toUpperCase() +
                                hub.hubType.slice(1)}
                            </Badge>
                          ) : (
                            <Badge bg="light" text="dark">
                              —
                            </Badge>
                          )}
                        </td>
                        <td className="align-middle">
                          {getOrderModeBadge(hub.orderMode || "preorder")}
                        </td>
                        <td className="align-middle">
                          <Badge bg="info" className="fs-6">
                            {formatCurrency(hub.minCart || 0)}
                          </Badge>
                        </td>
                        <td className="align-middle">
                          {getOfferBadge(hub.hubOffer)}
                          {hub.hubOffer && hub.hubOffer.isActive && (
                            <div className="mt-1">
                              <small className="text-muted">
                                {hub.hubOffer.currentUserCount || 0}/
                                {hub.hubOffer.userCap} used
                              </small>
                              <ProgressBar
                                now={
                                  ((hub.hubOffer.currentUserCount || 0) /
                                    hub.hubOffer.userCap) *
                                  100
                                }
                                size="sm"
                                className="mt-1"
                                variant={
                                  (hub.hubOffer.currentUserCount || 0) /
                                    hub.hubOffer.userCap >
                                  0.8
                                    ? "danger"
                                    : "success"
                                }
                              />
                            </div>
                          )}
                        </td>
                        <td className="align-middle">
                          <Accordion>
                            <Accordion.Item eventKey="0">
                              <Accordion.Header className="p-0">
                                <small>View Cutoff Times</small>
                              </Accordion.Header>
                              <Accordion.Body className="p-2">
                                <div className="small">
                                  <div className="mb-1">
                                    <Badge bg="secondary">Breakfast</Badge>
                                    <div>
                                      Regular:{" "}
                                      {hub.cutoffTimes?.breakfast
                                        ?.defaultCutoff || "00:00"}
                                    </div>
                                    <div>
                                      Employee:{" "}
                                      {hub.cutoffTimes?.breakfast
                                        ?.employeeCutoff || "10:00"}
                                    </div>
                                  </div>
                                  <div className="mb-1">
                                    <Badge bg="secondary">Lunch</Badge>
                                    <div>
                                      Regular:{" "}
                                      {hub.cutoffTimes?.lunch?.defaultCutoff ||
                                        "00:00"}
                                    </div>
                                    <div>
                                      Employee:{" "}
                                      {hub.cutoffTimes?.lunch?.employeeCutoff ||
                                        "10:00"}
                                    </div>
                                  </div>
                                  <div>
                                    <Badge bg="secondary">Dinner</Badge>
                                    <div>
                                      Regular:{" "}
                                      {hub.cutoffTimes?.dinner?.defaultCutoff ||
                                        "00:00"}
                                    </div>
                                    <div>
                                      Employee:{" "}
                                      {hub.cutoffTimes?.dinner
                                        ?.employeeCutoff || "10:00"}
                                    </div>
                                  </div>
                                </div>
                              </Accordion.Body>
                            </Accordion.Item>
                          </Accordion>
                        </td>
                        <td className="align-middle">
                          <div className="d-flex gap-1 flex-wrap">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => {
                                setEditHub({
                                  hubId: hub.hubId,
                                  hubName: hub.hubName,
                                  counterName: hub.counterName || "",
                                  hubType: hub.hubType || null,
                                  locations: hub.locations || [],
                                  geometry: hub.geometry || null,
                                  orderMode: hub.orderMode || "preorder",
                                  minCart: hub.minCart || 0,
                                  cutoffTimes:
                                    hub.cutoffTimes ||
                                    getDefaultCutoffTimes(
                                      hub.orderMode || "preorder",
                                    ),
                                });
                                setOriginalGeometry(hub.geometry || null);
                                setReassignPreview(null);
                                setShowEditHub(true);
                              }}
                              disabled={loading}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={() => {
                                setSelectedHubForCutoff({
                                  hubId: hub.hubId,
                                  hubName: hub.hubName,
                                  orderMode: hub.orderMode || "preorder",
                                  cutoffTimes: {
                                    breakfast: hub.cutoffTimes?.breakfast || {
                                      defaultCutoff: "00:00",
                                      employeeCutoff: "10:00",
                                    },
                                    lunch: hub.cutoffTimes?.lunch || {
                                      defaultCutoff: "00:00",
                                      employeeCutoff: "10:00",
                                    },
                                    dinner: hub.cutoffTimes?.dinner || {
                                      defaultCutoff: "00:00",
                                      employeeCutoff: "10:00",
                                    },
                                  },
                                });
                                setShowCutoffSettings(true);
                              }}
                              disabled={loading}
                            >
                              ⏰ Cutoff
                            </Button>
                            <Button
                              variant="outline-warning"
                              size="sm"
                              onClick={() => {
                                setSelectedHubForOrderMode({
                                  hubId: hub.hubId,
                                  hubName: hub.hubName,
                                  orderMode: hub.orderMode || "preorder",
                                });
                                setShowOrderModeModal(true);
                              }}
                              disabled={loading}
                            >
                              🔄 Mode
                            </Button>
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => {
                                setSelectedHubForMinCart({
                                  hubId: hub.hubId,
                                  hubName: hub.hubName,
                                  minCart: hub.minCart || 0,
                                });
                                setShowMinCartModal(true);
                              }}
                              disabled={loading}
                            >
                              💰 Min Cart
                            </Button>
                            <Button
                              variant="outline-dark"
                              size="sm"
                              onClick={() => {
                                const off = hub.hubOffer || null;
                                const startStr = off?.startDate
                                  ? new Date(off.startDate)
                                      .toISOString()
                                      .split("T")[0]
                                  : "";
                                const endStr = off?.endDate
                                  ? new Date(off.endDate)
                                      .toISOString()
                                      .split("T")[0]
                                  : "";
                                setSelectedHubForOffer({
                                  hubId: hub.hubId,
                                  hubName: hub.hubName,
                                  currentOffer: off,
                                });
                                setOfferConfig({
                                  userCap: off?.userCap || "",
                                  offerCap: off?.offerCap || "",
                                  // The saved per-user validity wins; the dates
                                  // only seed it for an offer that has none yet.
                                  validity:
                                    off?.validity ||
                                    computeValidityDays(startStr, endStr) ||
                                    "",
                                  mealsPerValidity: off?.mealsPerValidity || "",
                                  isActive:
                                    off?.isActive !== undefined
                                      ? off.isActive
                                      : true,
                                  startDate: startStr,
                                  endDate: endStr,
                                });
                                setShowOfferModal(true);
                              }}
                              disabled={loading}
                            >
                              🎁 Offer
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => fetchOfferStats(hub.hubId)}
                              disabled={loading || !hub.hubOffer}
                            >
                              📊 Stats
                            </Button>
                            {hub.hubOffer && hub.hubOffer.isActive && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDisableOffer(hub.hubId)}
                                disabled={loading || offerLoading}
                              >
                                ⏸️ Disable
                              </Button>
                            )}
                            {hub.hubOffer &&
                              !hub.hubOffer.isActive &&
                              hub.hubOffer.currentUserCount <
                                hub.hubOffer.userCap && (
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  onClick={() => handleEnableOffer(hub.hubId)}
                                  disabled={loading || offerLoading}
                                >
                                  ▶️ Enable
                                </Button>
                              )}
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => {
                                setSelectedHub(hub);
                                setShowDeleteHub(true);
                              }}
                              disabled={loading}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                ) : (
                  <tr>
                    <td colSpan={9} className="text-center py-4">
                      <div className="text-muted">
                        {search
                          ? "No hubs found matching your search."
                          : "No hubs available."}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {/* Pagination */}
          {hubs.length > hubsPerPage && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="text-muted">
                Showing {pagesVisited + 1} to{" "}
                {Math.min(pagesVisited + hubsPerPage, hubs.length)} of{" "}
                {hubs.length} entries
              </div>
              <ReactPaginate
                previousLabel="← Previous"
                nextLabel="Next →"
                pageCount={pageCount}
                onPageChange={changePage}
                containerClassName="pagination mb-0"
                previousLinkClassName="page-link"
                nextLinkClassName="page-link"
                disabledClassName="disabled"
                activeClassName="active"
                pageLinkClassName="page-link"
                pageClassName="page-item"
                previousClassName="page-item"
                nextClassName="page-item"
              />
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Add Hub Modal */}
      <Modal
        show={showAddHub}
        onHide={() => setShowAddHub(false)}
        size="xl"
        fullscreen
        style={{ zIndex: 99999 }}
      >
        <Modal.Header
          closeButton
          className="text-white"
          style={{ background: "#fe4500" }}
        >
          <Modal.Title className="text-white">Add New Hub</Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ maxHeight: "80vh", overflowY: "auto" }}>
          <Tabs defaultActiveKey="basic" className="mb-3">
            <Tab eventKey="basic" title="Basic Information">
              <Form>
                <Form.Group className="mb-4">
                  <Form.Label className="fw-bold">Hub Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={newHub.hubName}
                    onChange={(e) =>
                      setNewHub({ ...newHub, hubName: e.target.value })
                    }
                    placeholder="Enter hub name"
                    className="shadow-sm"
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label className="fw-bold">
                    Counter Name{" "}
                    <span style={{ fontWeight: 400, color: "#888" }}>
                      (prefix for order IDs)
                    </span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={newHub.counterName}
                    onChange={(e) =>
                      setNewHub({
                        ...newHub,
                        counterName: e.target.value.toUpperCase().slice(0, 5),
                      })
                    }
                    placeholder="e.g. GRE, MAN (max 5 chars)"
                    className="shadow-sm"
                    maxLength={5}
                  />
                  <Form.Text className="text-muted">
                    Used as prefix for counter IDs (e.g. GRE01, GRE02…). Falls
                    back to first 3 letters of hub name if not set.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label className="fw-bold">Hub Type</Form.Label>
                  <Form.Select
                    value={newHub.hubType || ""}
                    onChange={(e) =>
                      setNewHub({ ...newHub, hubType: e.target.value || null })
                    }
                    className="shadow-sm"
                  >
                    <option value="">-- Select Hub Type --</option>
                    <option value="school">School</option>
                    <option value="office">Office</option>
                    <option value="apartment">Apartment</option>
                    <option value="home">Home</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    If set to School, customers will be prompted for
                    student/teacher details when adding this hub as their
                    address.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label className="fw-bold">
                    Minimum Cart Value (₹)
                  </Form.Label>
                  <Form.Control
                    type="number"
                    value={newHub.minCart}
                    onChange={(e) =>
                      setNewHub({
                        ...newHub,
                        minCart: parseFloat(e.target.value) || 0,
                      })
                    }
                    placeholder="Enter minimum cart amount"
                    className="shadow-sm"
                    min="0"
                    step="0.01"
                  />
                  <Form.Text className="text-muted">
                    Minimum order value required for this hub (applies to all
                    meal sessions)
                  </Form.Text>
                </Form.Group>

                <OrderModeSelector
                  orderMode={newHub.orderMode}
                  onChange={handleNewHubOrderModeChange}
                  disabled={addHubLoading}
                />

                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">
                    Locations ({newHub.locations.length} selected)
                  </Form.Label>
                  <LocationSelector
                    selectedLocations={newHub.locations}
                    onLocationChange={(locations) =>
                      setNewHub({ ...newHub, locations })
                    }
                    disabled={addHubLoading}
                  />
                  {allLocations.length === 0 && (
                    <Form.Text className="text-danger">
                      No locations available. Please add locations first.
                    </Form.Text>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">
                    Service Area (Polygon)
                  </Form.Label>
                  <div className="border rounded overflow-hidden">
                    <AreaSelector
                      apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ""}
                      value={newHub.geometry}
                      onGeoJSONChange={(feature) =>
                        setNewHub({ ...newHub, geometry: feature })
                      }
                      editable={!addHubLoading}
                    />
                  </div>
                  <Form.Text muted>
                    Draw the hub's service area. This will be saved with the
                    hub.
                  </Form.Text>
                </Form.Group>
              </Form>
            </Tab>

            <Tab eventKey="offer" title="New Customer Offer (Optional)">
              <div className="p-3">
                <Alert variant="info">
                  <strong>🎁 New Customer Offer Configuration</strong>
                  <br />
                  Configure an offer for new customers registering at this hub.
                  This offer will be automatically assigned to new customers
                  only.
                </Alert>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">
                    Enable Offer for New Customers
                  </Form.Label>
                  <Form.Check
                    type="switch"
                    id="enable-offer-switch"
                    label={newHub.hubOffer ? "Enabled" : "Disabled"}
                    checked={!!newHub.hubOffer}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setNewHub({
                          ...newHub,
                          hubOffer: {
                            userCap: "",
                            offerCap: "",
                            validity: "",
                            mealsPerValidity: "",
                            startDate: "",
                            endDate: "",
                            isActive: true,
                          },
                        });
                      } else {
                        setNewHub({ ...newHub, hubOffer: null });
                      }
                    }}
                  />
                </Form.Group>

                {newHub.hubOffer && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">User Cap</Form.Label>
                      <Form.Control
                        type="number"
                        value={newHub.hubOffer.userCap}
                        onChange={(e) =>
                          setNewHub({
                            ...newHub,
                            hubOffer: {
                              ...newHub.hubOffer,
                              userCap: e.target.value,
                            },
                          })
                        }
                        placeholder="Maximum number of users who can claim this offer"
                        min="1"
                      />
                      <Form.Text className="text-muted">
                        Once this cap is reached, the offer will close
                        automatically
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">
                        Offer Cap (€ per day)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        value={newHub.hubOffer.offerCap}
                        onChange={(e) =>
                          setNewHub({
                            ...newHub,
                            hubOffer: {
                              ...newHub.hubOffer,
                              offerCap: e.target.value,
                            },
                          })
                        }
                        placeholder="Maximum discount value per day (e.g., 79, 99)"
                        min="0"
                        step="0.01"
                      />
                      <Form.Text className="text-muted">
                        Maximum discount amount per customer per day in Euros
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">
                        Meals per Validity
                      </Form.Label>
                      <Form.Control
                        type="number"
                        value={newHub.hubOffer.mealsPerValidity}
                        onChange={(e) =>
                          setNewHub({
                            ...newHub,
                            hubOffer: {
                              ...newHub.hubOffer,
                              mealsPerValidity: e.target.value,
                            },
                          })
                        }
                        placeholder="Number of free meals within validity window"
                        min="1"
                      />
                      <Form.Text className="text-muted">
                        Example: 6 free meals over 7 days
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">
                        Campaign Start Date{" "}
                        <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={newHub.hubOffer.startDate || ""}
                        onChange={(e) => {
                          const startDate = e.target.value;
                          setNewHub((prev) => ({
                            ...prev,
                            hubOffer: {
                              ...prev.hubOffer,
                              startDate,
                              // Only a suggestion — never overwrite a typed value.
                              validity:
                                prev.hubOffer.validity ||
                                computeValidityDays(
                                  startDate,
                                  prev.hubOffer.endDate,
                                ),
                            },
                          }));
                        }}
                      />
                      <Form.Text className="text-muted">
                        Offer goes live on this date
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">
                        Campaign End Date <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={newHub.hubOffer.endDate || ""}
                        min={newHub.hubOffer.startDate || undefined}
                        onChange={(e) => {
                          const endDate = e.target.value;
                          setNewHub((prev) => ({
                            ...prev,
                            hubOffer: {
                              ...prev.hubOffer,
                              endDate,
                              validity:
                                prev.hubOffer.validity ||
                                computeValidityDays(
                                  prev.hubOffer.startDate,
                                  endDate,
                                ),
                            },
                          }));
                        }}
                      />
                      <Form.Text className="text-muted">
                        No one can avail the offer after this date
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">
                        Validity (days) <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        value={newHub.hubOffer.validity}
                        onChange={(e) =>
                          setNewHub({
                            ...newHub,
                            hubOffer: {
                              ...newHub.hubOffer,
                              validity: e.target.value,
                            },
                          })
                        }
                        placeholder="Days each customer gets from their signup date"
                        min="1"
                      />
                      <Form.Text className="text-muted">
                        Per-customer window, counted from the day they register
                        — not the campaign length. Pre-filled from the campaign
                        dates; override it freely (e.g. 7 days inside a 30-day
                        campaign).
                      </Form.Text>
                    </Form.Group>

                    <Alert variant="warning">
                      <strong>Note:</strong> Per day limit is fixed at 1 free
                      meal per day and cannot be changed.
                    </Alert>
                  </>
                )}
              </div>
            </Tab>

            <Tab eventKey="cutoff" title="Cutoff Times">
              <div className="p-3">
                <Alert
                  variant={
                    newHub.orderMode === "preorder" ? "warning" : "success"
                  }
                >
                  <strong>
                    Current Order Mode:{" "}
                    {newHub.orderMode === "preorder"
                      ? "📋 Preorder"
                      : "⚡ Instant"}
                  </strong>
                  <br />
                  {newHub.orderMode === "preorder" ? (
                    <>
                      • <strong>Regular Customers:</strong> Must order by cutoff
                      time on the <strong>PREVIOUS day</strong>
                      <br />• <strong>Employees:</strong> Can order on the{" "}
                      <strong>SAME day</strong> until cutoff time
                    </>
                  ) : (
                    <>
                      • <strong>Both Regular Customers & Employees:</strong> Can
                      order on the <strong>SAME day</strong> until cutoff time
                    </>
                  )}
                </Alert>

                <CutoffTimeInput
                  label="Breakfast Session"
                  session="breakfast"
                  cutoffTimes={newHub.cutoffTimes}
                  orderMode={newHub.orderMode}
                  onChange={(session, type, value) => {
                    setNewHub({
                      ...newHub,
                      cutoffTimes: {
                        ...newHub.cutoffTimes,
                        [session]: {
                          ...newHub.cutoffTimes[session],
                          [type]: value,
                        },
                      },
                    });
                  }}
                />

                <CutoffTimeInput
                  label="Lunch Session"
                  session="lunch"
                  cutoffTimes={newHub.cutoffTimes}
                  orderMode={newHub.orderMode}
                  onChange={(session, type, value) => {
                    setNewHub({
                      ...newHub,
                      cutoffTimes: {
                        ...newHub.cutoffTimes,
                        [session]: {
                          ...newHub.cutoffTimes[session],
                          [type]: value,
                        },
                      },
                    });
                  }}
                />

                <CutoffTimeInput
                  label="Dinner Session"
                  session="dinner"
                  cutoffTimes={newHub.cutoffTimes}
                  orderMode={newHub.orderMode}
                  onChange={(session, type, value) => {
                    setNewHub({
                      ...newHub,
                      cutoffTimes: {
                        ...newHub.cutoffTimes,
                        [session]: {
                          ...newHub.cutoffTimes[session],
                          [type]: value,
                        },
                      },
                    });
                  }}
                />
              </div>
            </Tab>
          </Tabs>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowAddHub(false)}
            disabled={addHubLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddHub}
            disabled={addHubLoading}
          >
            {addHubLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Adding...
              </>
            ) : (
              "Add Hub"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Hub Modal */}
      <Modal
        show={showEditHub}
        onHide={() => setShowEditHub(false)}
        size="lg"
        fullscreen
        style={{ zIndex: 99999 }}
      >
        <Modal.Header
          closeButton
          className="text-white"
          style={{ background: "#fe4500" }}
        >
          <Modal.Title>Edit Hub</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "80vh", overflowY: "auto" }}>
          <Form>
            <Form.Group className="mb-4">
              <Form.Label className="fw-bold">Hub Name</Form.Label>
              <Form.Control
                type="text"
                value={editHub.hubName}
                onChange={(e) =>
                  setEditHub({ ...editHub, hubName: e.target.value })
                }
                placeholder="Enter hub name"
                className="shadow-sm"
                required
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className="fw-bold">
                Counter Name{" "}
                <span style={{ fontWeight: 400, color: "#888" }}>
                  (prefix for order IDs)
                </span>
              </Form.Label>
              <Form.Control
                type="text"
                value={editHub.counterName || ""}
                onChange={(e) =>
                  setEditHub({
                    ...editHub,
                    counterName: e.target.value.toUpperCase().slice(0, 5),
                  })
                }
                placeholder="e.g. GRE, MAN (max 5 chars)"
                className="shadow-sm"
                maxLength={5}
              />
              <Form.Text className="text-muted">
                Used as prefix for counter IDs (e.g. GRE01, GRE02…). Falls back
                to first 3 letters of hub name if not set.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className="fw-bold">Hub Type</Form.Label>
              <Form.Select
                value={editHub.hubType || ""}
                onChange={(e) =>
                  setEditHub({ ...editHub, hubType: e.target.value || null })
                }
                className="shadow-sm"
              >
                <option value="">-- Select Hub Type --</option>
                <option value="school">School</option>
                <option value="office">Office</option>
                <option value="apartment">Apartment</option>
                <option value="home">Home</option>
              </Form.Select>
              <Form.Text className="text-muted">
                If set to School, customers will be prompted for student/teacher
                details when adding this hub as their address.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className="fw-bold">
                Minimum Cart Value (₹)
              </Form.Label>
              <Form.Control
                type="number"
                value={editHub.minCart}
                onChange={(e) =>
                  setEditHub({
                    ...editHub,
                    minCart: parseFloat(e.target.value) || 0,
                  })
                }
                placeholder="Enter minimum cart amount"
                className="shadow-sm"
                min="0"
                step="0.01"
              />
              <Form.Text className="text-muted">
                Minimum order value required for this hub (applies to all meal
                sessions)
              </Form.Text>
            </Form.Group>

            <OrderModeSelector
              orderMode={editHub.orderMode}
              onChange={handleEditHubOrderModeChange}
              disabled={editHubLoading}
            />

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                Locations ({editHub.locations.length} selected)
              </Form.Label>
              <LocationSelector
                selectedLocations={editHub.locations}
                onLocationChange={(locations) =>
                  setEditHub({ ...editHub, locations })
                }
                disabled={editHubLoading}
              />
              {allLocations.length === 0 && (
                <Form.Text className="text-danger">
                  No locations available. Please add locations first.
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                Service Area (Polygon)
              </Form.Label>
              <div className="border rounded overflow-hidden">
                <AreaSelector
                  apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ""}
                  value={editHub.geometry}
                  onGeoJSONChange={(feature) =>
                    setEditHub({ ...editHub, geometry: feature })
                  }
                  editable={!editHubLoading}
                />
              </div>
              <Form.Text muted>
                Draw or update the hub's service area. Leave unchanged to keep
                existing polygon.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowEditHub(false)}
            disabled={editHubLoading}
          >
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={handleEditHub}
            disabled={editHubLoading || reassignLoading}
          >
            {editHubLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Updating...
              </>
            ) : reassignLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Checking impact...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Unserved Users Modal */}
      <Modal
        show={showUnservedPanel}
        onHide={() => setShowUnservedPanel(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
            Unserved Users ({unservedLoading ? "…" : unservedUsers.length})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{ maxHeight: "60vh", overflowY: "auto", padding: 0 }}
        >
          {unservedLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading...
            </div>
          ) : unservedUsers.length === 0 ? (
            <div className="text-center py-4 text-muted">
              All addresses are assigned to a hub.
            </div>
          ) : (
            <table className="table table-sm table-hover mb-0">
              <thead
                style={{ position: "sticky", top: 0, background: "#f8f9fa" }}
              >
                <tr>
                  <th style={{ fontSize: 12 }}>Name</th>
                  <th style={{ fontSize: 12 }}>Mobile</th>
                  <th style={{ fontSize: 12 }}>Unserved Addresses</th>
                </tr>
              </thead>
              <tbody>
                {unservedUsers.map((u, i) => (
                  <tr key={i}>
                    <td style={{ fontSize: 13 }}>{u.name}</td>
                    <td style={{ fontSize: 13 }}>{u.mobile}</td>
                    <td style={{ fontSize: 12 }}>
                      {u.unservedAddresses.map((a, j) => (
                        <div key={j} className="text-muted">
                          {a.address}
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowUnservedPanel(false)}
          >
            Close
          </Button>
          <Button
            variant="outline-secondary"
            onClick={fetchUnservedUsers}
            disabled={unservedLoading}
          >
            Refresh
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reassign Preview Modal */}
      <Modal
        show={showReassignPreview}
        onHide={() => setShowReassignPreview(false)}
        centered
      >
        <Modal.Header
          closeButton
          style={{ background: "#fff3cd", borderBottom: "1px solid #ffc107" }}
        >
          <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
            Polygon change will affect users
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {reassignPreview && (
            <div>
              {reassignPreview.addedCount > 0 && (
                <div
                  className="mb-2 p-2 rounded"
                  style={{ background: "#d4edda" }}
                >
                  <strong>{reassignPreview.addedCount}</strong> previously
                  unserved user{reassignPreview.addedCount !== 1 ? "s" : ""}{" "}
                  will now be added to this hub
                </div>
              )}
              {reassignPreview.removedToOtherHub > 0 && (
                <div
                  className="mb-2 p-2 rounded"
                  style={{ background: "#fff3cd" }}
                >
                  <strong>{reassignPreview.removedToOtherHub}</strong> user
                  {reassignPreview.removedToOtherHub !== 1 ? "s" : ""} will be
                  moved to a different hub
                </div>
              )}
              {reassignPreview.removedToNoHub > 0 && (
                <div
                  className="mb-2 p-2 rounded"
                  style={{ background: "#f8d7da" }}
                >
                  <strong>{reassignPreview.removedToNoHub}</strong> user
                  {reassignPreview.removedToNoHub !== 1 ? "s" : ""} will have{" "}
                  <strong>no hub</strong> and cannot place orders
                </div>
              )}
              {reassignPreview.removedUsers?.length > 0 && (
                <details className="mt-2">
                  <summary
                    style={{ cursor: "pointer", fontSize: 13, color: "#666" }}
                  >
                    View affected users ({reassignPreview.removedUsers.length})
                  </summary>
                  <div
                    className="mt-2"
                    style={{ maxHeight: 200, overflowY: "auto", fontSize: 12 }}
                  >
                    {reassignPreview.removedUsers.map((u, i) => (
                      <div key={i} className="border-bottom py-1">
                        <strong>{u.name}</strong> ({u.mobile}) &rarr;{" "}
                        {u.movingTo}
                        <div className="text-muted">{u.address}</div>
                      </div>
                    ))}
                  </div>
                </details>
              )}
              <div className="mt-3 text-muted" style={{ fontSize: 12 }}>
                Active orders already placed are not affected by this change.
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowReassignPreview(false)}
          >
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={doSaveHub}
            disabled={editHubLoading}
          >
            {editHubLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              "Save & Reassign"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Order Mode Modal */}
      <Modal
        show={showOrderModeModal}
        onHide={() => setShowOrderModeModal(false)}
        style={{ zIndex: 99999 }}
      >
        <Modal.Header
          closeButton
          className="text-white"
          style={{ background: "#fe4500" }}
        >
          <Modal.Title>
            Change Order Mode - {selectedHubForOrderMode?.hubName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <strong>Changing order mode will:</strong>
            <ul className="mt-2 mb-0">
              <li>Update cutoff time defaults for all sessions</li>
              <li>Propagate changes to all customer addresses in this hub</li>
              <li>Update existing orders and meal plans</li>
            </ul>
          </Alert>

          {selectedHubForOrderMode && (
            <>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Current Mode</Form.Label>
                <div>
                  {getOrderModeBadge(selectedHubForOrderMode.orderMode)}
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Select New Mode</Form.Label>
                <div className="d-flex gap-3">
                  <Form.Check
                    type="radio"
                    id="modal-preorder"
                    label={
                      <span>
                        <Badge bg="warning" text="dark" className="me-2">
                          📋 Preorder
                        </Badge>
                        <span className="text-muted">
                          (Previous day cutoff)
                        </span>
                      </span>
                    }
                    name="modalOrderMode"
                    value="preorder"
                    checked={selectedHubForOrderMode.orderMode === "preorder"}
                    onChange={() =>
                      setSelectedHubForOrderMode({
                        ...selectedHubForOrderMode,
                        orderMode: "preorder",
                      })
                    }
                  />
                  <Form.Check
                    type="radio"
                    id="modal-instant"
                    label={
                      <span>
                        <Badge bg="success" className="me-2">
                          ⚡ Instant
                        </Badge>
                        <span className="text-muted">(Same day cutoff)</span>
                      </span>
                    }
                    name="modalOrderMode"
                    value="instant"
                    checked={selectedHubForOrderMode.orderMode === "instant"}
                    onChange={() =>
                      setSelectedHubForOrderMode({
                        ...selectedHubForOrderMode,
                        orderMode: "instant",
                      })
                    }
                  />
                </div>
              </Form.Group>

              <Alert variant="warning">
                <strong>Note:</strong> This change will affect all customers,
                orders, and meal plans associated with this hub.
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowOrderModeModal(false)}
            disabled={orderModeLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateOrderMode}
            disabled={orderModeLoading}
          >
            {orderModeLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              "Update Order Mode"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Min Cart Modal */}
      <Modal
        show={showMinCartModal}
        onHide={() => setShowMinCartModal(false)}
        style={{ zIndex: 99999 }}
      >
        <Modal.Header
          closeButton
          className="text-white"
          style={{ background: "#fe4500" }}
        >
          <Modal.Title>
            Update Minimum Cart Value - {selectedHubForMinCart?.hubName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <strong>Updating minimum cart value will:</strong>
            <ul className="mt-2 mb-0">
              <li>Change the minimum order amount required for this hub</li>
              <li>Apply to all meal sessions (breakfast, lunch, dinner)</li>
              <li>Propagate changes to all customer addresses in this hub</li>
              <li>Update existing orders and meal plans</li>
            </ul>
          </Alert>

          {selectedHubForMinCart && (
            <>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">
                  Current Minimum Cart
                </Form.Label>
                <div>
                  <Badge bg="info" className="fs-6">
                    {formatCurrency(selectedHubForMinCart.minCart)}
                  </Badge>
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">
                  New Minimum Cart Value (₹)
                </Form.Label>
                <Form.Control
                  type="number"
                  value={selectedHubForMinCart.minCart}
                  onChange={(e) =>
                    setSelectedHubForMinCart({
                      ...selectedHubForMinCart,
                      minCart: parseFloat(e.target.value) || 0,
                    })
                  }
                  placeholder="Enter minimum cart amount"
                  min="0"
                  step="0.01"
                />
                <Form.Text className="text-muted">
                  Minimum order value required for all meal sessions
                </Form.Text>
              </Form.Group>

              <Alert variant="warning">
                <strong>Note:</strong> This change will affect all customers,
                orders, and meal plans associated with this hub. Orders below
                this amount will not be allowed.
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowMinCartModal(false)}
            disabled={minCartLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateMinCart}
            disabled={minCartLoading}
          >
            {minCartLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              "Update Min Cart"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Bulk Min Cart Modal */}
      <Modal
        show={showBulkMinCartModal}
        onHide={() => setShowBulkMinCartModal(false)}
        style={{ zIndex: 99999 }}
      >
        <Modal.Header
          closeButton
          className="text-white"
          style={{ background: "#fe4500" }}
        >
          <Modal.Title>Bulk Update Minimum Cart Value</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <strong>
              This will update the minimum cart value for ALL hubs.
            </strong>
            <ul className="mt-2 mb-0">
              <li>Apply the same minimum cart value to every hub</li>
              <li>Affect all meal sessions across all hubs</li>
              <li>Propagate changes to all customer addresses</li>
              <li>Update all existing orders and meal plans</li>
            </ul>
          </Alert>

          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">
              Minimum Cart Value for All Hubs (₹)
            </Form.Label>
            <Form.Control
              type="number"
              value={bulkMinCartValue}
              onChange={(e) =>
                setBulkMinCartValue(parseFloat(e.target.value) || 0)
              }
              placeholder="Enter minimum cart amount for all hubs"
              min="0"
              step="0.01"
            />
            <Form.Text className="text-muted">
              This value will be applied to every hub in the system
            </Form.Text>
          </Form.Group>

          <Alert variant="warning">
            <strong>Warning:</strong> This action cannot be undone and will
            affect:
            <ul className="mt-2 mb-0">
              <li>
                <strong>{hubs.length}</strong> hubs
              </li>
              <li>All customers associated with these hubs</li>
              <li>All existing and future orders</li>
              <li>All meal plans</li>
            </ul>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowBulkMinCartModal(false)}
            disabled={bulkMinCartLoading}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleBulkUpdateMinCart}
            disabled={bulkMinCartLoading}
          >
            {bulkMinCartLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Updating All Hubs...
              </>
            ) : (
              `Apply ₹${bulkMinCartValue} to All Hubs`
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Hub Offer Modal */}
      <Modal
        show={showOfferModal}
        onHide={() => setShowOfferModal(false)}
        size="lg"
        style={{ zIndex: 99999 }}
      >
        <Modal.Header
          closeButton
          className="text-white"
          style={{ background: "#fe4500" }}
        >
          <Modal.Title>
            Configure New Customer Offer - {selectedHubForOffer?.hubName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <strong>🎁 New Customer Offer Configuration</strong>
            <br />
            This offer will be automatically assigned to new customers who
            register at this hub. Existing customers will not receive this
            offer.
          </Alert>

          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">User Cap</Form.Label>
            <Form.Control
              type="number"
              value={offerConfig.userCap}
              onChange={(e) =>
                setOfferConfig({ ...offerConfig, userCap: e.target.value })
              }
              placeholder="Maximum number of users who can claim this offer"
              min="1"
            />
            <Form.Text className="text-muted">
              Example: 500 users. Once the cap is reached, the offer closes
              automatically.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">Offer Cap (€ per day)</Form.Label>
            <Form.Control
              type="number"
              value={offerConfig.offerCap}
              onChange={(e) =>
                setOfferConfig({ ...offerConfig, offerCap: e.target.value })
              }
              placeholder="Maximum discount value per day"
              min="0"
              step="0.01"
            />
            <Form.Text className="text-muted">
              Maximum discount value per day. Can be set to €79, €99, or any
              value. Applied per day, not per slot.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">Meals per Validity</Form.Label>
            <Form.Control
              type="number"
              value={offerConfig.mealsPerValidity}
              onChange={(e) =>
                setOfferConfig({
                  ...offerConfig,
                  mealsPerValidity: e.target.value,
                })
              }
              placeholder="Number of free meals within validity window"
              min="1"
            />
            <Form.Text className="text-muted">
              Example: 6 meals over 7 days.
            </Form.Text>
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">
                  Campaign Start Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  value={offerConfig.startDate}
                  onChange={(e) => {
                    const startDate = e.target.value;
                    setOfferConfig((prev) => ({
                      ...prev,
                      startDate,
                      // Only a suggestion — never overwrite a typed value.
                      validity:
                        prev.validity ||
                        computeValidityDays(startDate, prev.endDate),
                    }));
                  }}
                />
                <Form.Text className="text-muted">
                  Offer goes live on this date
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">
                  Campaign End Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  value={offerConfig.endDate}
                  min={offerConfig.startDate || undefined}
                  onChange={(e) => {
                    const endDate = e.target.value;
                    setOfferConfig((prev) => ({
                      ...prev,
                      endDate,
                      validity:
                        prev.validity ||
                        computeValidityDays(prev.startDate, endDate),
                    }));
                  }}
                />
                <Form.Text className="text-muted">
                  No one can avail the offer after this date
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">
              Validity (days) <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              value={offerConfig.validity}
              onChange={(e) =>
                setOfferConfig({ ...offerConfig, validity: e.target.value })
              }
              placeholder="Days each customer gets from their signup date"
              min="1"
            />
            <Form.Text className="text-muted">
              Per-customer window, counted from the day they register — not the
              campaign length. Pre-filled from the campaign dates; override it
              freely (e.g. 7 days inside a 30-day campaign). Unused meals expire
              when this window closes.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">Offer Status</Form.Label>
            <Form.Check
              type="switch"
              id="offer-active-switch"
              label={offerConfig.isActive ? "Active" : "Inactive"}
              checked={offerConfig.isActive}
              onChange={(e) =>
                setOfferConfig({ ...offerConfig, isActive: e.target.checked })
              }
            />
            <Form.Text className="text-muted">
              When active, new customers can claim this offer (until user cap is
              reached).
            </Form.Text>
          </Form.Group>

          <Alert variant="warning">
            <strong>Note:</strong> Per day limit is fixed at 1 free meal per day
            and cannot be changed.
          </Alert>

          {selectedHubForOffer?.currentOffer && (
            <Alert variant="secondary">
              <strong>Current Offer Details:</strong>
              <ul className="mt-2 mb-0">
                <li>User Cap: {selectedHubForOffer.currentOffer.userCap}</li>
                <li>
                  Offer Cap:{" "}
                  {formatOfferCap(selectedHubForOffer.currentOffer.offerCap)}
                  /day
                </li>
                <li>
                  Validity: {selectedHubForOffer.currentOffer.validity} days
                </li>
                <li>
                  Meals: {selectedHubForOffer.currentOffer.mealsPerValidity}{" "}
                  meals
                </li>
                <li>
                  Users Claimed:{" "}
                  {selectedHubForOffer.currentOffer.currentUserCount || 0}
                </li>
                <li>
                  Remaining Spots:{" "}
                  {selectedHubForOffer.currentOffer.remainingSpots ||
                    selectedHubForOffer.currentOffer.userCap}
                </li>
                {selectedHubForOffer.currentOffer.startDate && (
                  <li>
                    Campaign Start:{" "}
                    {new Date(
                      selectedHubForOffer.currentOffer.startDate,
                    ).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </li>
                )}
                {selectedHubForOffer.currentOffer.endDate && (
                  <li>
                    Campaign End:{" "}
                    <strong
                      style={{
                        color:
                          new Date(selectedHubForOffer.currentOffer.endDate) <
                          new Date()
                            ? "#dc3545"
                            : "inherit",
                      }}
                    >
                      {new Date(
                        selectedHubForOffer.currentOffer.endDate,
                      ).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                      {new Date(selectedHubForOffer.currentOffer.endDate) <
                        new Date() && " (Expired)"}
                    </strong>
                  </li>
                )}
              </ul>
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowOfferModal(false)}
            disabled={offerLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateHubOffer}
            disabled={offerLoading}
          >
            {offerLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              "Save Offer Configuration"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Offer Statistics Modal */}
      <Modal
        show={showOfferStatsModal}
        onHide={() => setShowOfferStatsModal(false)}
        size="lg"
        style={{ zIndex: 99999 }}
      >
        <Modal.Header
          closeButton
          className="text-white"
          style={{ background: "#fe4500" }}
        >
          <Modal.Title>Offer Statistics - {offerStats?.hubName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {offerStats && offerStats.offer && (
            <>
              <Card className="mb-3">
                <Card.Body>
                  <Card.Title>Offer Configuration</Card.Title>
                  <Row>
                    <Col md={6}>
                      <div className="mb-2">
                        <strong>User Cap:</strong> {offerStats.offer.userCap}
                      </div>
                      <div className="mb-2">
                        <strong>Offer Cap:</strong>{" "}
                        {formatOfferCap(offerStats.offer.offerCap)}/day
                      </div>
                      <div className="mb-2">
                        <strong>Validity:</strong> {offerStats.offer.validity}{" "}
                        days
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-2">
                        <strong>Meals per Validity:</strong>{" "}
                        {offerStats.offer.mealsPerValidity}
                      </div>
                      <div className="mb-2">
                        <strong>Per Day Limit:</strong>{" "}
                        {offerStats.offer.perDayLimit} meal
                      </div>
                      <div className="mb-2">
                        <strong>Status:</strong>{" "}
                        {offerStats.offer.isActive ? (
                          <Badge bg="success">Active</Badge>
                        ) : (
                          <Badge bg="danger">Inactive</Badge>
                        )}
                      </div>
                      {offerStats.offer.startDate && (
                        <div className="mb-2">
                          <strong>Campaign Start:</strong>{" "}
                          {new Date(
                            offerStats.offer.startDate,
                          ).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </div>
                      )}
                      {offerStats.offer.endDate && (
                        <div className="mb-2">
                          <strong>Campaign End:</strong>{" "}
                          <span
                            style={{
                              color:
                                new Date(offerStats.offer.endDate) < new Date()
                                  ? "#dc3545"
                                  : "inherit",
                              fontWeight: 600,
                            }}
                          >
                            {new Date(
                              offerStats.offer.endDate,
                            ).toLocaleDateString("en-IN", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })}
                            {new Date(offerStats.offer.endDate) < new Date() &&
                              " ⚠️ Expired"}
                          </span>
                        </div>
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Card>
                <Card.Body>
                  <Card.Title>Usage Statistics</Card.Title>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <span>Spots Utilization:</span>
                      <strong>
                        {offerStats.statistics.spotsFilled} /{" "}
                        {offerStats.offer.userCap}
                      </strong>
                    </div>
                    <ProgressBar
                      now={parseFloat(offerStats.statistics.utilizationRate)}
                      label={`${offerStats.statistics.utilizationRate}`}
                      variant={
                        parseFloat(offerStats.statistics.utilizationRate) > 80
                          ? "danger"
                          : parseFloat(offerStats.statistics.utilizationRate) >
                              50
                            ? "warning"
                            : "success"
                      }
                    />
                  </div>

                  <Row>
                    <Col md={6}>
                      <div className="mb-2">
                        <strong>Spots Remaining:</strong>{" "}
                        <Badge bg="info">
                          {offerStats.statistics.spotsRemaining}
                        </Badge>
                      </div>
                      <div className="mb-2">
                        <strong>Spots Filled:</strong>{" "}
                        {offerStats.statistics.spotsFilled}
                      </div>
                      <div className="mb-2">
                        <strong>Total Users Claimed:</strong>{" "}
                        {offerStats.statistics.totalUsersClaimed}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-2">
                        <strong>Customers with Active Offer:</strong>{" "}
                        {offerStats.statistics.customersWithActiveOffer}
                      </div>
                      <div className="mb-2">
                        <strong>Users who claimed offer (registered):</strong>{" "}
                        {offerStats.statistics.totalCustomersEverHadOffer}
                      </div>
                      <div className="mb-2">
                        <strong>Total orders placed with free meal:</strong>{" "}
                        <Badge bg="primary">
                          {offerStats.statistics.totalOfferOrdersPlaced ?? 0}
                        </Badge>
                        <div
                          className="text-muted"
                          style={{ fontSize: "11px", marginTop: "2px" }}
                        >
                          e.g. customer A used 5 + customer B used 4 = 9 total
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowOfferStatsModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Cutoff Settings Modal */}
      <Modal
        show={showCutoffSettings}
        onHide={() => setShowCutoffSettings(false)}
        size="lg"
        style={{ zIndex: 99999 }}
      >
        <Modal.Header
          closeButton
          className="text-white"
          style={{ background: "#fe4500" }}
        >
          <Modal.Title>
            Cutoff Time Settings - {selectedHubForCutoff?.hubName}
            <span className="ms-2">
              {selectedHubForCutoff &&
                getOrderModeBadge(selectedHubForCutoff.orderMode)}
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert
            variant={
              selectedHubForCutoff?.orderMode === "preorder"
                ? "warning"
                : "success"
            }
          >
            <strong>
              Current Order Mode:{" "}
              {selectedHubForCutoff?.orderMode === "preorder"
                ? "📋 Preorder"
                : "⚡ Instant"}
            </strong>
            <ul className="mt-2 mb-0">
              {selectedHubForCutoff?.orderMode === "preorder" ? (
                <>
                  <li>
                    <strong>Regular Customers:</strong> Must place orders by the
                    cutoff time on the <strong>previous day</strong>
                  </li>
                  <li>
                    <strong>Employees:</strong> Can place orders on the{" "}
                    <strong>same day</strong> until the cutoff time
                  </li>
                </>
              ) : (
                <>
                  <li>
                    <strong>Both Regular Customers & Employees:</strong> Can
                    place orders on the <strong>same day</strong> until the
                    cutoff time
                  </li>
                </>
              )}
            </ul>
          </Alert>

          {selectedHubForCutoff && (
            <>
              <CutoffTimeInput
                label="Breakfast Session"
                session="breakfast"
                cutoffTimes={selectedHubForCutoff.cutoffTimes}
                orderMode={selectedHubForCutoff.orderMode}
                onChange={(session, type, value) => {
                  setSelectedHubForCutoff({
                    ...selectedHubForCutoff,
                    cutoffTimes: {
                      ...selectedHubForCutoff.cutoffTimes,
                      [session]: {
                        ...selectedHubForCutoff.cutoffTimes[session],
                        [type]: value,
                      },
                    },
                  });
                }}
              />

              <CutoffTimeInput
                label="Lunch Session"
                session="lunch"
                cutoffTimes={selectedHubForCutoff.cutoffTimes}
                orderMode={selectedHubForCutoff.orderMode}
                onChange={(session, type, value) => {
                  setSelectedHubForCutoff({
                    ...selectedHubForCutoff,
                    cutoffTimes: {
                      ...selectedHubForCutoff.cutoffTimes,
                      [session]: {
                        ...selectedHubForCutoff.cutoffTimes[session],
                        [type]: value,
                      },
                    },
                  });
                }}
              />

              <CutoffTimeInput
                label="Dinner Session"
                session="dinner"
                cutoffTimes={selectedHubForCutoff.cutoffTimes}
                orderMode={selectedHubForCutoff.orderMode}
                onChange={(session, type, value) => {
                  setSelectedHubForCutoff({
                    ...selectedHubForCutoff,
                    cutoffTimes: {
                      ...selectedHubForCutoff.cutoffTimes,
                      [session]: {
                        ...selectedHubForCutoff.cutoffTimes[session],
                        [type]: value,
                      },
                    },
                  });
                }}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCutoffSettings(false)}
            disabled={cutoffLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateCutoffTimes}
            disabled={cutoffLoading}
          >
            {cutoffLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              "Save Cutoff Times"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Hub Modal */}
      <Modal show={showDeleteHub} onHide={() => setShowDeleteHub(false)}>
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title>Delete Hub</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <div className="mb-3">
              <i
                className="fas fa-exclamation-triangle text-danger"
                style={{ fontSize: "3rem" }}
              ></i>
            </div>
            <p className="mb-2">Are you sure you want to delete this hub?</p>
            <div className="alert alert-warning">
              <strong>Hub Name:</strong> {selectedHub?.hubName}
              <br />
              <strong>Hub ID:</strong> {selectedHub?.hubId}
              <br />
              <strong>Order Mode:</strong>{" "}
              {selectedHub?.orderMode || "preorder"}
              <br />
              <strong>Min Cart:</strong>{" "}
              {formatCurrency(selectedHub?.minCart || 0)}
              <br />
              <strong>Locations:</strong> {selectedHub?.locations?.length || 0}
              {selectedHub?.hubOffer && (
                <>
                  <br />
                  <strong>Has Offer:</strong> Yes
                  <br />
                  <strong>Users Claimed:</strong>{" "}
                  {selectedHub.hubOffer.currentUserCount || 0} /{" "}
                  {selectedHub.hubOffer.userCap}
                </>
              )}
            </div>
            <p className="text-muted">This action cannot be undone.</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteHub(false)}
            disabled={deleteHubLoading}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteHub}
            disabled={deleteHubLoading}
          >
            {deleteHubLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              "Delete Hub"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View All Polygons Modal */}
      <Modal
        show={showViewAllPolygons}
        onHide={() => setShowViewAllPolygons(false)}
        size="xl"
        fullscreen
        style={{ zIndex: 99999 }}
      >
        <Modal.Header
          closeButton
          className="text-white"
          style={{ background: "#fe4500" }}
        >
          <Modal.Title>All Hub Service Areas</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "85vh", padding: 0 }}>
          <AreaSelector
            apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ""}
            value={null}
            allPolygons={hubs
              .filter((hub) => hub.geometry)
              .map((hub) => ({
                geometry: hub.geometry,
                hubName: hub.hubName,
                hubId: hub.hubId,
                orderMode: hub.orderMode,
                minCart: hub.minCart,
              }))}
            editable={false}
            viewOnly={true}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowViewAllPolygons(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default HubList;
